import {
    Component,
    computed,
    inject,
    signal,
} from '@angular/core';

import {
    filter,
    merge
} from 'rxjs';

import { ActiveSaleActionButtonsComponent } from "../active-sale-action-buttons";

import {
    PaymentMethodTypeClass,
    paymentMethodTypes
} from 'tilby-models'

import {
    ScreenOrientationService
} from "src/app/core";

import { TranslateService } from '@ngx-translate/core';

import {
    BadgeInputButton,
    BaseInputButton,
    LongPressInputButton
} from '@tilby/tilby-ui-lib/models';

import {
    $state,
} from "app/ajs-upgraded-providers";

import {
    ConfirmDialogWithRememberService,
    GenericListDialogService,
    ItemListSelectorDialogService,
    PendingPrintsDialogService,
    PrinterErrorFiscalDialogService
} from 'src/app/dialogs';

import {
    CommonModule,
} from '@angular/common';

import {
    ActiveSaleService,
    ActiveSaleStoreService,
    SalePrintingUtilsService,
    SaleUpdatesData
} from 'src/app/features/cashregister';

import { ActiveSalePaymentService } from 'src/app/features/cashregister/services/active-sale-payment.service';

import { SaleMode } from 'src/app/shared/model/cashregister.model';
import { CashregisterKeyboardComponent } from "../../cashregister-keyboard";
import { KeyboardParentToAddNewItemToSaleComponent } from '../../../keyboard-parent-to-add-new-item-to-sale';
import { subscribeInComponent } from "src/app/shared/angular-utils";
import { DocumentPrinterOptions } from 'src/app/shared/model/document-printer.model';

@Component({
    selector: 'app-active-sale-action-buttons-wrapper',
    standalone: true,
    templateUrl: './active-sale-action-buttons-wrapper.component.html',
    imports: [
        ActiveSaleActionButtonsComponent,
        CommonModule,
        CashregisterKeyboardComponent
    ],
    providers: [ActiveSalePaymentService],
    styleUrls: ['./active-sale-action-buttons-wrapper.component.scss'],
})
export class ActiveSaleActionButtonsWrapperComponent extends KeyboardParentToAddNewItemToSaleComponent {
    private readonly state = inject($state);
    private readonly activeSalePaymentService = inject(ActiveSalePaymentService);
    private readonly genericListDialogService = inject(GenericListDialogService);
    private readonly translateService = inject(TranslateService);
    private readonly salePrintingUtilsService = inject(SalePrintingUtilsService);
    private readonly pendingPrintsDialogService = inject(PendingPrintsDialogService);
    private readonly confirmDialogWithRememberService = inject(ConfirmDialogWithRememberService);
    private readonly printerErrorFiscalService = inject(PrinterErrorFiscalDialogService);
    private readonly screenOrientationService = inject(ScreenOrientationService);
    private readonly itemListSelector = inject(ItemListSelectorDialogService);
    
    protected sideKeyboard = this.cashregisterStateService.sideKeyboard;
    protected isMobilePortrait = this.screenOrientationService.isMobilePortrait;

    protected isLoading = computed(() =>
        this.activeSaleService.printDocumentInProgress() ||
        this.activeSaleService.paymentInProgress()
    );

    protected sale? = this.activeSaleService.isActiveSale() ? this.activeSaleService.currentSale : undefined;
    protected saleAmount = 0;
    private hasPaidPayments = false;

    private exitArray$ = this.cashregisterStateService.exitsArray$;
    private ordersExitEnabled = true;

    protected actionButtons: BadgeInputButton[] = this.calculateOrderButtonMobile('bill');
    protected fastPaymentsButtons: LongPressInputButton[] = [];
    protected layoutActionButtons: SaleMode = 'order';
    protected principalActionButton: BaseInputButton | undefined;
    protected actionButtonsHeight = this.calculateActionButtonsHeight();

    // Configurations
    private readonly acceptsPayments = this.configurationManagerService.isUserPermitted('cashregister.accept_payments');
    private readonly skipPrinting = this.configurationManagerService.isUserPermitted('skip_printing');
    protected readonly showPrices = this.configurationManagerService.isUserPermitted('show_prices_ui');

    private calculateActionButtonsHeight() {
        if (this.isMobilePortrait()) {
            return 'tw-h-full';
        }

        if(!this.principalActionButton) {
            return 'tw-h-[114px]';
        }

        return this.fastPaymentsButtons.length > 0 ? 'tw-h-[145px]' : 'tw-h-[72px]';
    }

    private getOrderActionButtons(): BadgeInputButton[] {
        return [
            {
                name: 'CASHREGISTER.ACTIVE_SALE.ACTION_BUTTONS.PARKING',
                icon: signal('save'),
                click: () => this.save(),
            },
            ...(
                this.skipPrinting
                ? [{
                    name: 'CASHREGISTER.ACTIVE_SALE.ACTION_BUTTONS.WITHOUT_SEND',
                    icon: signal('cancel_schedule_send'),
                    click: () => this.cancelScheduleSend(),
                }]
                : []
            ),
            {
                name: 'CASHREGISTER.ACTIVE_SALE.ACTION_BUTTONS.SEND',
                icon: signal('send'),
                class: 'tilby-body-background-primary-color',
                click: () => this.send(),
            }
        ];
    }

    private saleChanged(updates: SaleUpdatesData | DocumentPrinterOptions) {
        // Check for SaleUpdatesData instead of DocumentPrinterOptions
        if('hasPaidPayments' in updates) {
            this.sale = updates.currentSale;
            this.layoutActionButtons = updates.saleMode || 'bill';
            this.hasPaidPayments = updates.hasPaidPayments;
        }

        let fastPaymentsButtons: LongPressInputButton[] = [];
        let actionButtons: BadgeInputButton[] = [];
        let principalActionButton: BaseInputButton | undefined;

        // Mobile Test
        if (this.isMobilePortrait()) {
            actionButtons = this.calculateOrderButtonMobile(this.layoutActionButtons);
        } else {
            switch (this.layoutActionButtons) {
                case 'order':
                    actionButtons = this.getOrderActionButtons();
                    break;
                case 'bill':
                    const isEnrc = this.checkIsEnrc();
                    const hasAllSaleItemsPriceNegative = this.hasAllSaleItemsPriceNegative();

                    fastPaymentsButtons = this.calculateFastPaymentButtons(isEnrc, hasAllSaleItemsPriceNegative);

                    principalActionButton = {
                        name: 'CASHREGISTER.ACTIVE_SALE.ACTION_BUTTONS.PAY',
                        click: () => this.pay(),
                        icon: signal(''),
                        isDisable: signal(!this.sale || !this.acceptsPayments || hasAllSaleItemsPriceNegative || isEnrc)
                    };
                    break;
            }
        }

        this.saleAmount = this.activeSaleService.getTotalAmountToPay();
        this.fastPaymentsButtons = fastPaymentsButtons;
        this.actionButtons = actionButtons;
        this.principalActionButton = principalActionButton;
        this.actionButtonsHeight = this.calculateActionButtonsHeight();
    }

    constructor() {
        super();

        // Subscriptions
        subscribeInComponent(
            this.cashregisterStateService.addItemToSale$.pipe(filter(() => this.sideKeyboard())),
            (data) => data.item && this.addItemToSale(data.item, data.combination_id, data.quantity, data.barcode)
        );

        subscribeInComponent(
            this.cashregisterStateService.addNewItemToSale$.pipe(filter(() => this.sideKeyboard())),
            () => this.addItemToSaleHandler('sale')
        );

        subscribeInComponent(
            this.cashregisterStateService.addDynamicCategoryItemToSale$.pipe(filter(() => this.sideKeyboard())),
            (data) => this.addDynamicCategoryItemToSale(data.category, data.price)
        );

        const cashregisterUpdates = merge(
            ActiveSaleService.printerDocumentDataUpdates$,
            ActiveSaleStoreService.saleUpdates$
        )

        subscribeInComponent(cashregisterUpdates, (updates) => this.saleChanged(updates));
    }

    // START - SALE CHANGED FUNCTIONS

    //MOBILE
    private calculateOrderButtonMobile(layout: SaleMode): BadgeInputButton[] {
        switch (layout) {
            case 'order':
                return <BadgeInputButton[]>[
                    ...(this.ordersExitEnabled && [
                        {
                            name: 'CASHREGISTER.ACTIVE_SALE.ACTION_BUTTONS.EXIT',
                            icon: signal('move_item'),
                            click: () => this.exit(),
                            badgeValue: -1,
                        },
                    ] || []),
                    ...this.getOrderActionButtons()
                ];
            case 'bill':
                const saleDisableSignal = signal(!this.sale);

                return <BadgeInputButton[]>[
                    {
                        name: 'CASHREGISTER.ACTIVE_SALE.ACTION_BUTTONS.PAY',
                        icon: signal('receipt_long'),
                        click: () => this.pay(),
                        isDisable: signal(!this.sale || !this.acceptsPayments),
                        class: !this.acceptsPayments ? 'button-disabled' : '',
                    }, {
                        name: 'CASHREGISTER.ACTIVE_SALE.ACTION_BUTTONS.SPLIT',
                        icon: signal('call_split'),
                        click: () => this.callSplit(),
                        isDisable: saleDisableSignal,
                    }, {
                        name: 'CASHREGISTER.ACTIVE_SALE.ACTION_BUTTONS.TRANSFER',
                        icon: signal('move_up'),
                        click: () => this.modifySale(),
                        isDisable: saleDisableSignal,
                    },
                    ...(this.ordersExitEnabled && [
                        {
                            name: 'CASHREGISTER.ACTIVE_SALE.ACTION_BUTTONS.EXIT',
                            icon: signal('move_item'),
                            click: () => this.exit(),
                            badgeValue: -1,
                            isDisable: saleDisableSignal,
                        },
                    ] || []),
                ];
            default:
                return [];
        }
    }
    /**
     *  Calculates the fast payment buttons based on the provided layout.
     *  @returns An array of fast payment buttons or undefined.
     */
    private calculateFastPaymentButtons(isEnrc: boolean, hasAllSaleItemsPriceNegative: boolean): LongPressInputButton[] {
        if (isEnrc) {
            return [
                {
                    name: 'CASHREGISTER.ACTIVE_SALE.CREATE_SUMMARY_INVOICE',
                    click: () => this.paySummary(),
                    longPress: () => undefined,
                    icon: signal('')
                }
            ];
        }

        if (hasAllSaleItemsPriceNegative) {
            return [
                {
                    name: 'CASHREGISTER.ACTIVE_SALE.RETURN_OR_CANCEL',
                    click: () => this.emitCreditNote(),
                    longPress: () => undefined,
                    icon: signal(''),
                    isDisable: signal(!this.acceptsPayments)
                }
            ];
        }

        const fastPayments: LongPressInputButton[] = [];
        
        // Check for customer payments restriction
        const paymentsAllowed = new Set<number>();

        if(this.sale?.sale_customer?.payments_allowed) {
            try {
                const parsed = JSON.parse(this.sale?.sale_customer?.payments_allowed);

                if(Array.isArray(parsed)) {
                    parsed.forEach(n => paymentsAllowed.add(n));
                }
            } catch(err) {
                // Do nothing
            }
        }

        const longpressDisabled = !!paymentsAllowed.size;

        const paymentDisabled = !this.sale || !this.acceptsPayments || this.hasPaidPayments || hasAllSaleItemsPriceNegative || isEnrc;

        for (let i = 1; i <= 3; i++) {
            const fastPaymentClass = this.configurationManagerService.getPreference(`cashregister.fast_payment_${i}_type`);

            if(!fastPaymentClass) {
                continue;
            }

            const disabled = signal(paymentDisabled);

            // Check if we need to disable the payment (e.g. if the customer cannot use it)
            if(!disabled() && paymentsAllowed.size) {
                //Keep the button disabled to avoid it being enabled for some frames, then check if we can enable it
                disabled.set(true);

                //Use a then callback to avoid making the whole function async, which can lead to some hard-to-debug race conditions
                this.getClassPaymentMethods(fastPaymentClass).then((paymentMethods) => {
                    /**
                        Payment button should be enabled if
                        - The user default payment method exists and is in the customer allowed list
                        - The only payment method for the button class is in the customer allowed list
                    */
                    const defaultPayment = this.getFastPaymentDefault(i);

                    // We can't enable without a default payment and with 2 or more (or no) payments for this button
                    if(!defaultPayment && paymentMethods.length !== 1) {
                        return;
                    }

                    // Find the payment method that is currently linked to the button
                    const paymentMethod = defaultPayment
                        ? paymentMethods.find((paymentMethod) => paymentMethod.id === defaultPayment) || paymentMethods[0]
                        : paymentMethods[0];
                    
                    // Payment is enabled if there is a method and that method is in the allowed list 
                    disabled.set(!paymentMethod || !paymentsAllowed.has(paymentMethod.id));
                });
            }

            const fastPaymentButton: LongPressInputButton = {
                name: this.configurationManagerService.getPreference(`cashregister.fast_payment_${i}_label`) || <string>this.translateService.instant(`PAYMENT_CLASSES.${(<string>fastPaymentClass).toUpperCase()}`),
                click: () => this.fastPayment(i, fastPaymentClass, 'click'),
                longPress: longpressDisabled ? () => {} : () => this.fastPayment(i, fastPaymentClass, 'longPress'),
                icon: signal(''),
                isDisable: disabled
            }

            if(fastPaymentClass === 'prepaid') {
                this.activeSaleService.getCustomerPrepaidInfo().then(customerCredit => {
                    if(customerCredit) {
                        fastPaymentButton.name = this.translateService.instant('CASHREGISTER.PAYMENTS.CREDIT', { value: this.tilbyCurrencyPipe.transform(customerCredit.total_credit || 0) });
                    }
                });
            }

            fastPayments.push(fastPaymentButton);
        }

        if (fastPayments.length) {
            return fastPayments;
        }

        return [];
    }

    // END - SALE CHANGED FUNCTIONS

    // START - BUTTON ACTION FUNCTIONS
    private pay() {
        this.goToPayments();
    }

    private async save() {
        const { room_id, order_type } = this.activeSaleService.currentSale || {};

        //Saves and closes the current sale, so after this step currentSale is empty
        await this.activeSaleService.saveSale(undefined, true);

        if (this.configurationManagerService.getPreference("orders.open_tables_after_park")) {
            this.activeSaleService.goToTablesSelectedRoom(room_id, order_type);
        }
    }

    private goToTables() {
        const { order_type, room_id } = this.activeSaleService.currentSale || {};
        this.activeSaleService.goToTablesSelectedRoom(room_id, order_type);
    }

    private goToTablesAfterSend() {
        if (this.configurationManagerService.getPreference("orders.open_tables_after_send")) {
            this.goToTables();
        }
    }

    private goToTablesAfterSkipSend() {
        if (this.configurationManagerService.getPreference("orders.open_tables_after_skip_send")) {
            this.goToTables();
        }
    }

    private async cancelScheduleSend() {
        const skipPreference = "dialog.skip_printing.skip_question";
        const skipQuestion = this.configurationManagerService.getPreference(skipPreference);

        if (!skipQuestion) {
            const response = await this.confirmDialogWithRememberService.openDialog({
                data: {
                    textContent: 'CASHREGISTER.ACTIVE_SALE.SKIP_TRANSACTION_PRINTING_CONFIRM',
                    textToggle: 'CASHREGISTER.ACTIVE_SALE.REMEMBER_CHOICE',
                    classTextContent: 'tw-pb-4'
                }
            });

            if (!response.confirm) {
                return;
            }

            if (response.toggleChoice) {
                this.configurationManagerService.setUserPreference(skipPreference, true);
            }
        }

        await this.activeSaleService.skipTransactionsSend();
        this.goToTablesAfterSkipSend();
    }


    private async send() {
        const results = await this.activeSaleService.sendTransactions();

        if (this.salePrintingUtilsService.getPrintErrors(results).length) {
            this.pendingPrintsDialogService.openDialog();
        } else {
            this.goToTablesAfterSend();
        }
    }

    private openPrinterErrorDialog(error: any) {
        if(error != null) {
            this.printerErrorFiscalService.openDialog({
                data: {
                    error,
                    options: {
                        printerId: this.activeSaleService.printerDocumentData?.printer.id
                    }
                }
            });
        }
    }

    private getFastPaymentPreference(index: number) {
        return `cashregister.fastpayment_${index}` as `cashregister.fastpayment_${number}`;
    }

    private getFastPaymentDefault(fastPaymentMethodIndex: number) {
        const defaultPaymentMethodPreference = this.getFastPaymentPreference(fastPaymentMethodIndex);
        const defaultPaymentMethodIdPref = this.configurationManagerService.getPreference(defaultPaymentMethodPreference);

        if(!defaultPaymentMethodIdPref) {
            return;
        }

        return parseInt(defaultPaymentMethodIdPref) || 0;
    }

    private async getClassPaymentMethods(fastPaymentMethod: PaymentMethodTypeClass) {
        //Find payment method types for the button class
        const paymentMethodTypeIds = paymentMethodTypes.filter((methodType) => methodType.class === fastPaymentMethod).map((methodType) => methodType.id);

        if (!paymentMethodTypeIds.length) {
            return [];
        }

        //Find available payment methods for the button class
        return await this.entityManagerService.paymentMethods.fetchCollectionOffline({ payment_method_type_id_in: paymentMethodTypeIds, hidden: false });
    }

    private async fastPayment(fastPaymentMethodIndex: number, fastPaymentMethod: PaymentMethodTypeClass, type: 'click' | 'longPress') {
        if (this.activeSaleService.paymentInProgress() || this.activeSaleService.lockPaymentButtons()) {
            return;
        }

        try {
            await this.activeSalePaymentService.beforePayment();

            //Find available payment methods for the button class
            const paymentMethods = await this.getClassPaymentMethods(fastPaymentMethod);

            //Get user default payment method id for this button if exists
            const defaultPaymentMethodId = this.getFastPaymentDefault(fastPaymentMethodIndex);

            let targetPaymentMethod = defaultPaymentMethodId
                ? paymentMethods.find((paymentMethod) => paymentMethod.id === defaultPaymentMethodId)
                : undefined;

            //Show a selection dialog if we cannot find the default payment method or the user is forcing the manual selection
            if (!targetPaymentMethod || type == 'longPress') {
                if (paymentMethods.length > 1) {
                    await this.itemListSelector.openDialog({ data: { message: 'CASHREGISTER.ACTIVE_SALE.SELECT_PAYMENT', items: paymentMethods, options: { defaultElement: targetPaymentMethod }} })
                    .then((selectResult) => {
                        //Save the selection if the user wants so
                        if (selectResult.setAsDefault) {
                            const pref = this.getFastPaymentPreference(fastPaymentMethodIndex);
                            this.configurationManagerService.setUserPreference(pref, selectResult.element.id);
                        }

                        targetPaymentMethod = selectResult.element;
                    });
                } else {
                    targetPaymentMethod = paymentMethods[0];
                }
            }

            if (!targetPaymentMethod) {
                return;
            }

            const paymentAmountForCashType = targetPaymentMethod.payment_method_type_id == 1 ? this.cashregisterStateService.keyboardTotalValue() : undefined;

            if (!!paymentAmountForCashType && (this.activeSaleService.currentSale.final_amount || 0) > (paymentAmountForCashType || 0)) {
                return this.alertDialogService.openDialog({ data: { messageLabel: 'CASHREGISTER.ACTIVE_SALE.PAYMENT_AMOUNT_EXCEEDS_FINAL_AMOUNT' } });
            }

            const amount = this.activeSaleService.getFastPaymentAmount(this.activeSaleService.currentSale, targetPaymentMethod, paymentAmountForCashType);
            await this.activeSaleService.addGenericPayment(targetPaymentMethod, amount, undefined, { clearPreviousPayments: true });

            await this.activeSalePaymentService.emitSale({ runSaleChecks: false });
        } catch (error) {
            this.openPrinterErrorDialog(error);
        }
    }

    //MOBILE ONLY
    private async exit() {
        const exitSelected = await this.genericListDialogService.openDialog({
            data: {
                title: { label: 'DIALOG.GENERIC_LIST_DIALOG.EXIT.TITLE' },
                list: this.exitArray$.getValue().map((exit) => ({
                    ...exit,
                    name: this.translateService.instant(exit.name, { value: exit.nameValue }),
                    value: exit,
                }))
            }
        });

        if (exitSelected) {
            this.cashregisterStateService.setExit(exitSelected);
        }
    }
    private callSplit() {
        if (!this.activeSaleService.isActiveSale()) {
            return;
        }

        if (!this.sale?.sale_items?.length) {
            return this.alertDialogService.openDialog({
                data: {
                    messageLabel: 'CASHREGISTER.ACTIVE_SALE_MODEL.NO_ITEM_IN_SALE'
                }
            });
        }

        this.activeSaleService.splitSale();
    }
    private modifySale() {
        if (this.activeSaleService.isActiveSale()) {
            this.activeSaleService.editSale();
        }
    }

    // END - BUTTON ACTION FUNCTIONS

    // START - PAYMENT FUNCTIONS

    public async goToPayments() {
        try {
            await this.activeSalePaymentService.beforePayment();
            this.state.go('app.new.cashregister.content.payments', {});
            ActiveSaleService.activeSaleEvents$.next({ event: 'go-to-payments' });
        } catch (error) {
            this.openPrinterErrorDialog(error);
        }
    };
    // END - PAYMENT FUNCTIONS

    emitCreditNote() {
        if (this.activeSaleService.paymentInProgress() || this.activeSaleService.lockPaymentButtons()) {
            return;
        }

        this.activeSalePaymentService.emitSale({ runSaleChecks: true }).catch((error) => {
            this.openPrinterErrorDialog(error);
        });
    };

    paySummary() {
        if (this.activeSaleService.paymentInProgress() || this.activeSaleService.lockPaymentButtons()) {
            return;
        }
        
        this.activeSalePaymentService.emitSale().catch((error) => {
            this.openPrinterErrorDialog(error);
        });
    };

    checkIsEnrc(): boolean {
        // France does not have unclaimed summary invoices
        if(this.configurationManagerService.getShopCountry() === 'FR') {
            return false;
        }

        return (this.sale?.is_summary && ['summary_e_nrc', 'generic_invoice'].includes(this.activeSaleService.printerDocumentData?.document_type?.id || '') || false);
    }

    private hasAllSaleItemsPriceNegative(): boolean {
        if (!this.sale?.sale_items?.length) {
            return false;
        }

        if (this.sale.sale_items.every((item) => (item.price * item.quantity) === 0)) {
            return false;
        }

        return this.sale.sale_items.every((item) => (item.price * item.quantity) <= 0);
    }
}
